import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';
import { AlertTriangle, RefreshCw, Wrench, CheckCircle2, XCircle, FileWarning } from 'lucide-react';

const fmtMoeda = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
const fmtData = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
};
const fmtDateTime = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const DiagnosticoPagamentosPage = () => {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [regenerando, setRegenerando] = useState(false);
  const [ultimoResultado, setUltimoResultado] = useState(null);
  const [selecionados, setSelecionados] = useState(new Set());

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const { data } = await apiService.api.get('/admin/diagnostico/pagamentos-faltantes');
      setDados(data);
      setSelecionados(new Set());
    } catch (e) {
      setErro(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const toggleSelecao = (id) => {
    setSelecionados(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleTodos = () => {
    if (!dados?.locacoes_sem_pendente) return;
    if (selecionados.size === dados.locacoes_sem_pendente.length) {
      setSelecionados(new Set());
    } else {
      setSelecionados(new Set(dados.locacoes_sem_pendente.map(l => l.locacao_id)));
    }
  };

  const regenerar = async (apenasSelecionadas) => {
    const ids = apenasSelecionadas ? Array.from(selecionados) : [];
    const qtd = apenasSelecionadas ? selecionados.size : (dados?.locacoes_sem_pendente?.length || 0);
    if (qtd === 0) return;
    const msg = apenasSelecionadas
      ? `Regenerar o próximo pagamento de ${qtd} locação(ões) selecionada(s)?`
      : `Regenerar o próximo pagamento de TODAS as ${qtd} locação(ões) detectadas?`;
    if (!window.confirm(msg)) return;

    setRegenerando(true);
    setUltimoResultado(null);
    try {
      const { data } = await apiService.api.post(
        '/admin/diagnostico/regenerar-pagamentos-faltantes',
        apenasSelecionadas ? { locacao_ids: ids } : {}
      );
      setUltimoResultado(data);
      await carregar();
    } catch (e) {
      setErro(e.response?.data?.detail || e.message);
    } finally {
      setRegenerando(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-slate-400" data-testid="diagnostico-loading">
        <RefreshCw className="w-6 h-6 mx-auto animate-spin mb-2" />
        Analisando locações ativas...
      </div>
    );
  }

  const faltantes = dados?.locacoes_sem_pendente || [];
  const totalAtivas = dados?.total_locacoes_ativas || 0;
  const totalPendentes = dados?.total_pagamentos_pendentes || 0;
  const falhasRecentes = dados?.falhas_recentes || [];

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="diagnostico-pagamentos-page">
      {/* HEADER */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Wrench className="w-7 h-7 text-amber-400" />
            Diagnóstico de Pagamentos
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Detecta locações ativas sem pagamento pendente — o sintoma do bug onde a confirmação falhou silenciosamente.
          </p>
        </div>
        <button
          onClick={carregar}
          className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition"
          data-testid="btn-recarregar-diagnostico"
        >
          <RefreshCw className="w-4 h-4" /> Recarregar
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <p className="text-xs text-slate-400 uppercase tracking-wider">Locações ativas</p>
          <p className="text-3xl font-bold text-white mt-1" data-testid="kpi-locacoes-ativas">{totalAtivas}</p>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <p className="text-xs text-slate-400 uppercase tracking-wider">Pagamentos em aberto</p>
          <p className="text-3xl font-bold text-white mt-1" data-testid="kpi-pagamentos-pendentes">{totalPendentes}</p>
        </div>
        <div className={`border rounded-xl p-5 ${faltantes.length > 0 ? 'bg-red-900/20 border-red-500/40' : 'bg-emerald-900/20 border-emerald-500/40'}`}>
          <p className="text-xs uppercase tracking-wider text-slate-400">Sem pagamento pendente</p>
          <p className={`text-3xl font-bold mt-1 ${faltantes.length > 0 ? 'text-red-400' : 'text-emerald-400'}`} data-testid="kpi-faltantes">
            {faltantes.length}
          </p>
          {faltantes.length === 0 ? (
            <p className="text-xs text-emerald-300 mt-2 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Todas as locações ativas têm pagamento pendente.
            </p>
          ) : (
            <p className="text-xs text-red-300 mt-2 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> Requer atenção.
            </p>
          )}
        </div>
      </div>

      {/* Erros */}
      {erro && (
        <div className="bg-red-900/30 border border-red-500/40 text-red-300 rounded-lg p-3" data-testid="diagnostico-erro">
          {erro}
        </div>
      )}

      {/* RESULTADO de regeneração */}
      {ultimoResultado && (
        <div className="bg-blue-900/20 border border-blue-500/40 rounded-xl p-4" data-testid="resultado-regeneracao">
          <p className="text-sm font-semibold text-blue-200 mb-2">
            Última regeneração: {ultimoResultado.total_criados} criado(s) / {ultimoResultado.total_falhos} falha(s)
          </p>
          {ultimoResultado.criados?.length > 0 && (
            <ul className="text-xs text-slate-300 space-y-1">
              {ultimoResultado.criados.slice(0, 5).map(c => (
                <li key={c.pagamento_id}>
                  ✅ {c.cliente_nome || '?'} — {c.placa || '?'} — Semana {c.semana} — {fmtMoeda(c.valor)}
                </li>
              ))}
            </ul>
          )}
          {ultimoResultado.falhos?.length > 0 && (
            <ul className="text-xs text-red-300 space-y-1 mt-2">
              {ultimoResultado.falhos.slice(0, 5).map((f, i) => (
                <li key={i}>❌ {f.cliente_nome || f.locacao_id?.slice(0,8)} — {f.erro}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* LISTA DE LOCAÇÕES FALTANTES */}
      {faltantes.length > 0 && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-700 flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-semibold text-white">Locações sem próximo pagamento ({faltantes.length})</h2>
            <div className="flex gap-2">
              <button
                onClick={() => regenerar(true)}
                disabled={regenerando || selecionados.size === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-400 disabled:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 rounded-lg text-xs font-semibold transition"
                data-testid="btn-regenerar-selecionados"
              >
                <Wrench className="w-3.5 h-3.5" />
                Regenerar selecionados ({selecionados.size})
              </button>
              <button
                onClick={() => regenerar(false)}
                disabled={regenerando}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:bg-slate-600 text-slate-900 rounded-lg text-xs font-semibold transition"
                data-testid="btn-regenerar-todos"
              >
                <Wrench className="w-3.5 h-3.5" />
                {regenerando ? 'Regenerando...' : `Regenerar TODOS (${faltantes.length})`}
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/60 text-slate-300">
                <tr>
                  <th className="px-3 py-2 text-left">
                    <input
                      type="checkbox"
                      checked={selecionados.size === faltantes.length && faltantes.length > 0}
                      onChange={toggleTodos}
                      data-testid="checkbox-selecionar-todos"
                    />
                  </th>
                  <th className="px-3 py-2 text-left">Cliente</th>
                  <th className="px-3 py-2 text-left">Placa</th>
                  <th className="px-3 py-2 text-left">Status loc.</th>
                  <th className="px-3 py-2 text-left">Último pagto.</th>
                  <th className="px-3 py-2 text-left">Próx. semana</th>
                  <th className="px-3 py-2 text-left">Próx. vencimento</th>
                  <th className="px-3 py-2 text-left">Motivo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/60">
                {faltantes.map(f => (
                  <tr key={f.locacao_id} className="hover:bg-slate-700/30" data-testid={`linha-faltante-${f.locacao_id.slice(0,8)}`}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selecionados.has(f.locacao_id)}
                        onChange={() => toggleSelecao(f.locacao_id)}
                        data-testid={`checkbox-${f.locacao_id.slice(0,8)}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-white">{f.cliente_nome || '-'}</td>
                    <td className="px-3 py-2 text-emerald-300 font-mono text-xs">{f.placa || '-'}</td>
                    <td className="px-3 py-2 text-slate-300">{f.status_locacao}</td>
                    <td className="px-3 py-2 text-slate-300">
                      {f.ultima_semana ? `Sem.${f.ultima_semana} — ${fmtData(f.ultima_data_vencimento)}` : 'nenhum'}
                      {f.ultimo_status && <span className="ml-1 text-xs text-slate-400">({f.ultimo_status})</span>}
                    </td>
                    <td className="px-3 py-2 text-amber-300 font-bold">{f.proxima_semana}</td>
                    <td className="px-3 py-2 text-slate-300">{fmtData(f.proxima_data_vencimento)}</td>
                    <td className="px-3 py-2 text-xs text-slate-400 max-w-xs">{f.motivo_provavel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* FALHAS RECENTES (P1) */}
      {falhasRecentes.length > 0 && (
        <div className="bg-orange-900/15 border border-orange-500/40 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-orange-500/30">
            <h2 className="font-semibold text-orange-200 flex items-center gap-2">
              <FileWarning className="w-5 h-5" />
              Falhas detectadas pelo guard ({falhasRecentes.length})
            </h2>
            <p className="text-xs text-orange-300/80 mt-1">
              Erros que ocorreram durante a criação automática do próximo pagamento — capturados pelo sistema com traceback completo.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/60 text-slate-300">
                <tr>
                  <th className="px-3 py-2 text-left">Quando</th>
                  <th className="px-3 py-2 text-left">Contexto</th>
                  <th className="px-3 py-2 text-left">Tipo</th>
                  <th className="px-3 py-2 text-left">Mensagem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/60">
                {falhasRecentes.map((f, i) => (
                  <tr key={f.id || i} className="hover:bg-slate-700/30">
                    <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{fmtDateTime(f.ocorrido_em)}</td>
                    <td className="px-3 py-2 text-slate-400 text-xs">{f.contexto}</td>
                    <td className="px-3 py-2 text-orange-300">{f.erro_tipo}</td>
                    <td className="px-3 py-2 text-slate-300 text-xs">{f.erro_mensagem}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Estado vazio */}
      {faltantes.length === 0 && falhasRecentes.length === 0 && !erro && (
        <div className="bg-emerald-900/20 border border-emerald-500/40 rounded-xl p-8 text-center" data-testid="diagnostico-tudo-ok">
          <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-emerald-300">Tudo certo!</h3>
          <p className="text-sm text-slate-400 mt-1">
            Todas as {totalAtivas} locações ativas têm pelo menos um pagamento pendente ou atrasado.
            Nenhuma falha recente registrada.
          </p>
        </div>
      )}
    </div>
  );
};

export default DiagnosticoPagamentosPage;
